import React, { useState } from 'react'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { useNavigate } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import NavbarSimple from '@/components/layout/navbar/navbar-simple'
import FooterSimple from '@/components/layout/footer/footer-simple'
import Icon from '@/components/shared/icons'
import { GoogleIcon } from '@/components/shared/svg-icons'
import InputGroupContent from '@/components/elements/input'
import { LoginModel } from '@/entities/api/customers/customer'
import {
	changePasswordWithCode,
	getCustomerByEmail,
	loginLocal,
	sendLoginCode,
	verifyLoginCode,
} from '@/services/api/auth/auth'
import Toast from '@/components/elements/toast'
import decodeJWT from '@/utils/decode-jwt'
import CustomerJwtPayload from '@/entities/api/jwt'
import { login } from '@/store/features/auth'

interface LoginFormValues {
	email: string
	password: string
}

interface CodeFormValues {
	code: number
}

interface RecoveryFormValues {
	email: string
}

interface ChangePasswordFormValues {
	code: number
	newPassword: string
	confirmPassword: string
}

const loginSchema = yup.object().shape({
	email: yup
		.string()
		.email('Correo electrónico inválido')
		.required('El correo es obligatorio'),
	password: yup
		.string()
		.min(6, 'La contraseña debe tener al menos 6 caracteres')
		.required('La contraseña es obligatoria'),
})

const codeSchema = yup.object().shape({
	code: yup
		.number()
		.required('El código es obligatorio')
		.typeError('El código debe ser un número'),
})

const recoverySchema = yup.object().shape({
	email: yup
		.string()
		.email('Correo electrónico inválido')
		.required('El correo es obligatorio'),
})

const changePasswordSchema = yup.object().shape({
	code: yup
		.number()
		.required('El código es obligatorio')
		.typeError('El código debe ser un número'),
	newPassword: yup
		.string()
		.min(6, 'La contraseña debe tener al menos 6 caracteres')
		.required('La nueva contraseña es obligatoria'),
	confirmPassword: yup
		.string()
		.oneOf([yup.ref('newPassword')], 'Las contraseñas no coinciden')
		.required('Confirma la nueva contraseña'),
})

const Login: React.FC = () => {
	const [step, setStep] = useState<
		'login' | 'code' | 'recovery' | 'change-password'
	>('login')
	const [isLoading, setIsLoading] = useState(false)
	const [userEmail, setUserEmail] = useState('')
	const navigate = useNavigate()
	const dispatch = useDispatch()

	const loginForm = useForm<LoginFormValues>({
		resolver: yupResolver(loginSchema),
		defaultValues: {
			email: '',
			password: '',
		},
	})

	const codeForm = useForm<CodeFormValues>({
		resolver: yupResolver(codeSchema),
		defaultValues: {
			code: 0,
		},
	})

	const recoveryForm = useForm<RecoveryFormValues>({
		resolver: yupResolver(recoverySchema),
		defaultValues: {
			email: '',
		},
	})

	const changePasswordForm = useForm<ChangePasswordFormValues>({
		resolver: yupResolver(changePasswordSchema),
		defaultValues: {
			code: 0,
			newPassword: '',
			confirmPassword: '',
		},
	})

	const handleLogin = async (data: LoginFormValues) => {
		setIsLoading(true)
		try {
			const loginData = {
				email: data.email,
				password: data.password,
			}

			const response = await loginLocal(loginData)

			if ([400, 404, 401, 409].includes(response?.status ?? 0)) {
				Toast({
					type: 'error',
					message: 'Credenciales incorrectas. Verifica tu correo y contraseña.',
				})
				setIsLoading(false)
				return
			}

			setUserEmail(data.email)
			setStep('code')
			Toast({
				type: 'success',
				message: 'Código de verificación enviado a tu correo.',
			})
		} catch (error) {
			Toast({
				type: 'error',
				message: 'Error al iniciar sesión. Intenta de nuevo.',
			})
		}
		setIsLoading(false)
	}

	const handleVerifyCode = async (data: CodeFormValues) => {
		setIsLoading(true)
		try {
			const response = await verifyLoginCode(data.code)

			if ([400, 404, 401, 409].includes(response?.status ?? 0)) {
				Toast({
					type: 'error',
					message: 'Código de verificación incorrecto.',
				})
				setIsLoading(false)
				return
			}

			const token = response.data
			const customerBase: CustomerJwtPayload | null = decodeJWT(token)

			if (!customerBase) {
				Toast({
					type: 'error',
					message: 'Error: no se pudo obtener el usuario, contacta al soporte.',
				})
				setIsLoading(false)
				return
			}

			const customerResponse = await getCustomerByEmail(
				customerBase?.email ?? '',
				token
			)

			if ([400, 404, 401, 409].includes(customerResponse?.status ?? 0)) {
				Toast({
					type: 'error',
					message: 'Error: no se pudo crear el usuario, contacta al soporte.',
				})
				setIsLoading(false)
				return
			}

			const customer = customerResponse.data
			dispatch(login({ token, customer }))
			navigate('/')
		} catch (error) {
			console.log(error)
			Toast({
				type: 'error',
				message: 'Error al verificar el código. Intenta de nuevo.',
			})
		}
		setIsLoading(false)
	}

	const handleSendRecoveryCode = async (data: RecoveryFormValues) => {
		setIsLoading(true)
		try {
			const response = await sendLoginCode(data.email)

			if ([400, 404, 401, 409].includes(response?.status ?? 0)) {
				Toast({
					type: 'error',
					message: 'No se pudo enviar el código. Verifica tu correo.',
				})
				setIsLoading(false)
				return
			}

			setUserEmail(data.email)
			setStep('change-password')
			Toast({
				type: 'success',
				message: 'Código de recuperación enviado a tu correo.',
			})
		} catch (error) {
			Toast({
				type: 'error',
				message: 'Error al enviar el código de recuperación.',
			})
		}
		setIsLoading(false)
	}

	const handleChangePassword = async (data: ChangePasswordFormValues) => {
		setIsLoading(true)
		try {
			const changeData: LoginModel = {
				email: userEmail,
				passwordCurrent: data.confirmPassword,
				passwordNew: data.newPassword,
			}

			const response = await changePasswordWithCode(data.code, changeData)

			if ([400, 404, 401, 409].includes(response?.status ?? 0)) {
				Toast({
					type: 'error',
					message: 'Error al cambiar la contraseña. Verifica el código.',
				})
				setIsLoading(false)
				return
			}

			Toast({
				type: 'success',
				message: 'Contraseña cambiada exitosamente.',
			})
			setStep('login')
			changePasswordForm.reset()
			recoveryForm.reset()
		} catch (error) {
			Toast({
				type: 'error',
				message: 'Error al cambiar la contraseña. Intenta de nuevo.',
			})
		}
		setIsLoading(false)
	}

	const renderLoginForm = () => (
		<div className='space-y-6'>
			<InputGroupContent
				label='Correo'
				placeholder='Ingresa tu correo electrónico'
				type='email'
				name='email'
				register={loginForm.register}
				errors={loginForm.formState.errors}
				required={true}
			/>
			<InputGroupContent
				label='Contraseña'
				placeholder='Ingresa tu contraseña'
				type='password'
				name='password'
				register={loginForm.register}
				errors={loginForm.formState.errors}
				required={true}
			/>
		</div>
	)

	const renderCodeForm = () => (
		<div className='space-y-6'>
			<div className='text-center'>
				<div className='flex justify-center items-center mb-5'>
					<div className='w-16 h-16 bg-red-100 rounded-full flex items-center justify-center shadow-lg border-4 border-red-50'>
						<svg
							className='w-8 h-8 text-red-600'
							fill='currentColor'
							viewBox='0 0 24 24'
						>
							<path d='M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z' />
						</svg>
					</div>
				</div>
				<h3 className='text-xl font-semibold text-gray-900 mb-3'>
					Código de verificación
				</h3>
				<p className='text-gray-600 text-sm leading-relaxed'>
					Hemos enviado un código de verificación a tu correo electrónico
				</p>
			</div>
			<InputGroupContent
				label='Código de verificación'
				placeholder='Ingresa el código de 6 dígitos'
				type='number'
				name='code'
				register={codeForm.register}
				errors={codeForm.formState.errors}
				required={true}
			/>
		</div>
	)

	const renderRecoveryForm = () => (
		<div className='space-y-6'>
			<div className='text-center'>
				<div className='flex justify-center items-center mb-5'>
					<div className='w-16 h-16 bg-red-100 rounded-full flex items-center justify-center shadow-lg border-4 border-red-50'>
						<svg
							className='w-8 h-8 text-red-600'
							fill='currentColor'
							viewBox='0 0 24 24'
						>
							<path d='M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zM9 6c0-1.66 1.34-3 3-3s3 1.34 3 3v2H9V6z' />
						</svg>
					</div>
				</div>
				<h3 className='text-xl font-semibold text-gray-900 mb-3'>
					Recuperar contraseña
				</h3>
				<p className='text-gray-600 text-sm leading-relaxed'>
					Ingresa tu correo para enviarte un código de recuperación
				</p>
			</div>
			<InputGroupContent
				label='Correo'
				placeholder='Ingresa tu correo electrónico'
				type='email'
				name='email'
				register={recoveryForm.register}
				errors={recoveryForm.formState.errors}
				required={true}
			/>
		</div>
	)

	const renderChangePasswordForm = () => (
		<div className='space-y-6'>
			<div className='text-center'>
				<div className='flex justify-center items-center mb-5'>
					<div className='w-16 h-16 bg-red-100 rounded-full flex items-center justify-center shadow-lg border-4 border-red-50'>
						<svg
							className='w-8 h-8 text-red-600'
							fill='currentColor'
							viewBox='0 0 24 24'
						>
							<path d='M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z' />
						</svg>
					</div>
				</div>
				<h3 className='text-xl font-semibold text-gray-900 mb-3'>
					Cambiar contraseña
				</h3>
				<p className='text-gray-600 text-sm leading-relaxed'>
					Ingresa el código y tu nueva contraseña
				</p>
			</div>
			<InputGroupContent
				label='Código de verificación'
				placeholder='Ingresa el código de 6 dígitos'
				type='number'
				name='code'
				register={changePasswordForm.register}
				errors={changePasswordForm.formState.errors}
				required={true}
			/>
			<InputGroupContent
				label='Nueva contraseña'
				placeholder='Ingresa tu nueva contraseña'
				type='password'
				name='newPassword'
				register={changePasswordForm.register}
				errors={changePasswordForm.formState.errors}
				required={true}
			/>
			<InputGroupContent
				label='Confirmar contraseña'
				placeholder='Confirma tu nueva contraseña'
				type='password'
				name='confirmPassword'
				register={changePasswordForm.register}
				errors={changePasswordForm.formState.errors}
				required={true}
			/>
		</div>
	)

	const getFormHandler = () => {
		switch (step) {
			case 'login':
				return loginForm.handleSubmit(handleLogin)
			case 'code':
				return codeForm.handleSubmit(handleVerifyCode)
			case 'recovery':
				return recoveryForm.handleSubmit(handleSendRecoveryCode)
			case 'change-password':
				return changePasswordForm.handleSubmit(handleChangePassword)
		}
	}

	const getSubmitButtonText = () => {
		switch (step) {
			case 'login':
				return 'Iniciar Sesión'
			case 'code':
				return 'Verificar Código'
			case 'recovery':
				return 'Enviar Código'
			case 'change-password':
				return 'Cambiar Contraseña'
		}
	}

	const getStepTitle = () => {
		switch (step) {
			case 'login':
				return 'Inicia sesión'
			case 'code':
				return 'Verificación'
			case 'recovery':
				return 'Recuperar contraseña'
			case 'change-password':
				return 'Cambiar contraseña'
		}
	}

	return (
		<div className='min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100'>
			<NavbarSimple />

			<div className='flex w-full px-4 xl:px-20 py-4 xl:py-8 min-h-[84vh] items-center flex-col md:flex-row'>
				<div className='hidden md:flex md:w-1/2 h-[70vh] relative overflow-hidden bg-gradient-to-br from-red-600 via-red-700 to-red-800 rounded-3xl shadow-2xl'>
					<div className='absolute inset-0 bg-black/5'></div>

					<div className='absolute inset-0'>
						<div className='absolute top-8 left-8 w-16 h-16 bg-white/10 rounded-full animate-pulse'></div>
						<div className='absolute top-20 right-12 w-10 h-10 bg-white/15 rotate-45 animate-bounce'></div>
						<div className='absolute bottom-24 left-16 w-14 h-14 bg-white/8 rounded-full'></div>
						<div className='absolute bottom-16 right-10 w-20 h-20 border-2 border-white/15 rounded-full'></div>

						<div className='absolute top-1/4 left-0 w-full h-0.5 bg-white/10 transform rotate-12'></div>
						<div className='absolute top-3/4 left-0 w-full h-0.5 bg-white/5 transform -rotate-12'></div>

						<div className='absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2'>
							<div
								className='w-40 h-40 border-4 border-white/20 rounded-full animate-spin'
								style={{ animationDuration: '20s' }}
							></div>
							<div
								className='absolute inset-6 border-2 border-white/30 rounded-full animate-spin'
								style={{
									animationDuration: '15s',
									animationDirection: 'reverse',
								}}
							></div>
						</div>
					</div>

					<div className='relative z-10 flex flex-col justify-center items-center p-8 text-white w-full'>
						<div className='max-w-sm text-center space-y-6'>
							<div>
								<div className='flex items-center justify-center mb-5'>
									<div className='w-16 h-16 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center shadow-xl border border-white/10'>
										<svg
											className='w-8 h-8 text-white'
											fill='currentColor'
											viewBox='0 0 24 24'
										>
											<path d='M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z' />
										</svg>
									</div>
								</div>
								<h1 className='text-3xl font-bold mb-3 leading-tight'>
									Bienvenido a{' '}
									<span className='text-red-200'>SportCenter Shop</span>
								</h1>
								<p className='text-lg text-red-100 leading-relaxed'>
									Tu destino para equipamiento deportivo de primera calidad
								</p>
							</div>

							<div className='space-y-3'>
								<div className='flex items-center space-x-3 bg-white/10 backdrop-blur-sm rounded-xl p-3 border border-white/20 hover:bg-white/15 transition-all duration-300'>
									<div className='w-10 h-10 bg-white/20 rounded-full flex items-center justify-center shrink-0'>
										<span className='text-lg'>💪</span>
									</div>
									<div className='text-left'>
										<h3 className='font-semibold text-white text-sm'>
											Entrena sin límites
										</h3>
										<p className='text-red-100 text-xs'>
											Equipamiento profesional
										</p>
									</div>
								</div>

								<div className='flex items-center space-x-3 bg-white/10 backdrop-blur-sm rounded-xl p-3 border border-white/20 hover:bg-white/15 transition-all duration-300'>
									<div className='w-10 h-10 bg-white/20 rounded-full flex items-center justify-center shrink-0'>
										<span className='text-lg'>🏆</span>
									</div>
									<div className='text-left'>
										<h3 className='font-semibold text-white text-sm'>
											Calidad garantizada
										</h3>
										<p className='text-red-100 text-xs'>
											Productos de primera línea
										</p>
									</div>
								</div>

								<div className='flex items-center space-x-3 bg-white/10 backdrop-blur-sm rounded-xl p-3 border border-white/20 hover:bg-white/15 transition-all duration-300'>
									<div className='w-10 h-10 bg-white/20 rounded-full flex items-center justify-center shrink-0'>
										<span className='text-lg'>⚡</span>
									</div>
									<div className='text-left'>
										<h3 className='font-semibold text-white text-sm'>
											Resultados rápidos
										</h3>
										<p className='text-red-100 text-xs'>
											Alcanza tus objetivos
										</p>
									</div>
								</div>
							</div>

							<div className='bg-gradient-to-r from-white/20 to-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/30 shadow-xl hover:shadow-2xl transition-all duration-300'>
								<p className='text-base font-semibold mb-1'>
									"Tu mejor versión te está esperando"
								</p>
								<p className='text-red-100 text-xs'>💬 Motivación diaria</p>
							</div>
						</div>
					</div>

					<div className='absolute bottom-6 left-6 right-6'>
						<button className='w-full cursor-pointer bg-white/20 backdrop-blur-sm border border-white/30 hover:bg-white/30 transition-all duration-300 rounded-xl p-3 flex items-center justify-center gap-2 group shadow-lg hover:shadow-xl'>
							<Icon icon='contract' size='lg' color='white' />
							<span className='text-white text-sm font-semibold group-hover:text-red-100 transition-colors duration-300'>
								Términos y condiciones
							</span>
						</button>
					</div>
				</div>

				<div className='w-full md:w-1/2 h-auto md:h-[70vh] relative pl-0 md:pl-6 flex justify-center items-center'>
					<div className='w-full max-w-lg'>
						<div className='bg-white rounded-3xl shadow-2xl border border-gray-100 overflow-hidden backdrop-blur-sm'>
							<div className='p-8 sm:p-10 lg:p-12'>
								<div className='text-center mb-8'>
									<div className='md:hidden flex justify-center mb-6'>
										<div className='w-16 h-16 bg-red-600 rounded-full flex items-center justify-center shadow-lg border-4 border-red-100'>
											<svg
												className='w-8 h-8 text-white'
												fill='currentColor'
												viewBox='0 0 24 24'
											>
												<path d='M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z' />
											</svg>
										</div>
									</div>
									<h2 className='text-2xl sm:text-3xl font-bold text-gray-900 mb-3'>
										{getStepTitle()}
									</h2>
									<div className='w-24 h-1 bg-gradient-to-r from-red-500 to-red-600 mx-auto rounded-full shadow-sm'></div>
								</div>

								<form onSubmit={getFormHandler()}>
									{step === 'login' && renderLoginForm()}
									{step === 'code' && renderCodeForm()}
									{step === 'recovery' && renderRecoveryForm()}
									{step === 'change-password' && renderChangePasswordForm()}

									<div className='mt-8 space-y-5'>
										<button
											type='submit'
											disabled={isLoading}
											className='w-full cursor-pointer bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white font-semibold py-4 px-6 rounded-xl shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none border border-red-500 hover:border-red-600'
										>
											{isLoading ? (
												<div className='flex items-center justify-center space-x-2'>
													<svg
														className='w-5 h-5 animate-spin'
														fill='none'
														viewBox='0 0 24 24'
													>
														<circle
															className='opacity-25'
															cx='12'
															cy='12'
															r='10'
															stroke='currentColor'
															strokeWidth='4'
														></circle>
														<path
															className='opacity-75'
															fill='currentColor'
															d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
														></path>
													</svg>
													<span>Procesando...</span>
												</div>
											) : (
												getSubmitButtonText()
											)}
										</button>

										{step === 'login' && (
											<>
												<button
													type='button'
													onClick={() => navigate('/Registro')}
													className='w-full mt-4 cursor-pointer bg-white border-2 border-red-200 text-red-600 hover:bg-red-50 hover:border-red-300 font-semibold py-4 px-6 rounded-xl transition-all duration-300 shadow-sm hover:shadow-md transform hover:-translate-y-0.5'
												>
													Crear cuenta nueva
												</button>

												<div className='text-center py-3'>
													<p className='text-gray-600'>
														¿Olvidaste tu contraseña?{' '}
														<button
															type='button'
															onClick={() => setStep('recovery')}
															className='cursor-pointer text-red-600 hover:text-red-700 font-semibold transition-colors duration-200 hover:underline'
														>
															Recupérala aquí
														</button>
													</p>
												</div>

												<div className='flex items-center my-6'>
													<div className='flex-1 h-px bg-gradient-to-r from-transparent via-gray-300 to-transparent'></div>
													<span className='px-6 text-gray-500 font-medium text-sm bg-white relative'>
														o continúa con
													</span>
													<div className='flex-1 h-px bg-gradient-to-r from-transparent via-gray-300 to-transparent'></div>
												</div>

												<button
													onClick={() =>
														Toast({
															type: 'info',
															message:
																'Funcionalidad en desarrollo, estara disponible muy pronto.',
														})
													}
													type='button'
													className='w-full cursor-pointer bg-white border-2 border-gray-200 text-gray-700 hover:bg-gray-50 hover:border-gray-300 font-semibold py-4 px-6 rounded-xl flex items-center justify-center gap-3 transition-all duration-300 shadow-sm hover:shadow-md transform hover:-translate-y-0.5'
												>
													<GoogleIcon />
													Google
												</button>
											</>
										)}

										{(step === 'code' ||
											step === 'recovery' ||
											step === 'change-password') && (
											<button
												type='button'
												onClick={() => {
													setStep('login')
													codeForm.reset()
													recoveryForm.reset()
													changePasswordForm.reset()
												}}
												className='w-full mt-4 cursor-pointer bg-white border-2 border-gray-200 text-gray-700 hover:bg-gray-50 hover:border-gray-300 font-semibold py-4 px-6 rounded-xl transition-all duration-300 shadow-sm hover:shadow-md transform hover:-translate-y-0.5'
											>
												← Volver al inicio de sesión
											</button>
										)}
									</div>
								</form>
							</div>
						</div>
					</div>
				</div>
			</div>

			<FooterSimple />
		</div>
	)
}

export default Login
